import type { ARCPattern, ARCPatternResult, LiveSnapshot } from "@/arc/arc.types";

export const collectionsOverduePattern: ARCPattern = {
  id: "collections_overdue",
  label: "Collections & Overdue Payments",
  owner: "finance",
  urgency: "medium",

  evaluate(snapshot: LiveSnapshot): ARCPatternResult {
    const { jobs } = snapshot;

    const jobRev = (j: typeof jobs[0]) =>
      j.financials?.contractValue ?? j.payoutAmount ?? 0;

    // Completed jobs not yet paid
    const completedUnpaid = jobs.filter(
      (j) =>
        j.currentStage === "Completed" &&
        j.financials &&
        j.financials.paymentStatus !== "Paid"
    );

    // Any job with overdue payment
    const overdue = jobs.filter(
      (j) => j.financials?.paymentStatus === "Overdue"
    );

    // Merge deduped
    const seen = new Set<string>();
    const flagged = [...completedUnpaid, ...overdue].filter((j) => {
      if (seen.has(j.id)) return false;
      seen.add(j.id);
      return true;
    });

    const amountAtRisk = flagged.reduce((s, j) => s + jobRev(j), 0);

    const summary = flagged.map((j) => {
      const status = j.financials?.paymentStatus ?? "Unknown";
      const completed = j.currentStage === "Completed";
      return `${j.customerName} — ${completed ? "Completed, " : ""}${status} — $${jobRev(j).toLocaleString()}`;
    });

    return {
      triggered: flagged.length > 0,
      count: flagged.length,
      affectedJobIds: flagged.map((j) => j.id),
      amountAtRisk,
      context:
        flagged.length > 0
          ? `${flagged.length} job${flagged.length > 1 ? "s" : ""} with uncollected balances — $${amountAtRisk.toLocaleString()} cash not received`
          : "All balances collected",
      suggestedAction:
        overdue.length > 0
          ? "Initiate collections immediately on overdue accounts — send final notice and escalate to principal"
          : "Send invoices and follow up on completed jobs with outstanding balances",
      snapshot: {
        flaggedJobIds: flagged.map((j) => j.id),
        completedUnpaidCount: completedUnpaid.length,
        overdueCount: overdue.length,
        summary,
        amountAtRisk,
      },
    };
  },
};
